import { apiFetch } from './client';
import { SUPPLIER_INTERIORS_ENDPOINTS } from './config';

/**
 * Normalize a single interior type from API response.
 * Response contract: camelCase only (id, interiorType, parentId, attachTo, interiorLibraryCount).
 */
function normalizeInteriorType(t) {
  const id = t.id;
  if (id == null) return null;
  return {
    id: Number(id),
    interiorType: t.interiorType ?? String(id),
    parentId: t.parentId ?? null,
    attachTo: t.attachTo ?? null,
    interiorLibraryCount: t.interiorLibraryCount ?? 0,
  };
}

/**
 * Fetch all interior types using the flat endpoint:
 * GET /supplier-interiors/interior-types
 * Backend now returns the full list without needing per-parent calls.
 * @returns {Promise<Array<{ id: number, interiorType: string, parentId: number | null, attachTo: unknown, interiorLibraryCount: number }>>}
 */
export async function getAllInteriorTypes() {
  const res = await apiFetch(SUPPLIER_INTERIORS_ENDPOINTS.interiorTypesList, { method: 'GET' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Failed to load interior types list (${res.status})`);
  }
  const data = await res.json().catch(() => ({}));
  const list = data.interiorTypes ?? [];
  const byId = new Map();
  for (const raw of list) {
    const normalized = normalizeInteriorType(raw);
    if (normalized && !byId.has(normalized.id)) {
      byId.set(normalized.id, normalized);
    }
  }
  const sorted = Array.from(byId.values()).sort((a, b) =>
    String(a.interiorType).localeCompare(String(b.interiorType))
  );
  // Same display name can appear more than once with different ids; keep one row for pickers/search.
  const seenNames = new Set();
  const deduped = [];
  for (const t of sorted) {
    const key = String(t.interiorType ?? '').trim().toLowerCase();
    if (!key || seenNames.has(key)) continue;
    seenNames.add(key);
    deduped.push(t);
  }
  return deduped;
}

/**
 * Fetch supplier materials list.
 * GET /api/v1/supplier-interiors/materials
 * Returns { materials: [...], total: number }
 */
export async function getMaterials() {
  const res = await apiFetch(SUPPLIER_INTERIORS_ENDPOINTS.materials, { method: 'GET' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Failed to load materials (${res.status})`);
  }
  const data = await res.json().catch(() => ({}));
  const list = Array.isArray(data.materials) ? data.materials : [];
  return {
    materials: list,
    total: typeof data.total === 'number' ? data.total : list.length,
  };
}

/**
 * Fetch supplier products list.
 * GET /api/v1/supplier-interiors/?page={page}&page_size={pageSize}
 * Returns { interiors: [...], total, page, pageSize }
 */
export async function getProducts(page = 1, pageSize = 10) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  const url = `${SUPPLIER_INTERIORS_ENDPOINTS.products}?${params.toString()}`;
  const res = await apiFetch(url, { method: 'GET' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Failed to load products (${res.status})`);
  }
  const data = await res.json().catch(() => ({}));
  const list = Array.isArray(data.interiors) ? data.interiors : [];
  return {
    products: list,
    total: typeof data.total === 'number' ? data.total : list.length,
    page: typeof data.page === 'number' ? data.page : page,
    pageSize: typeof data.pageSize === 'number' ? data.pageSize : pageSize,
  };
}

/**
 * Fetch materials by interior type id.
 * GET /api/v1/supplier-interiors/materials/by-interior-type/?interior-type-id={id}
 * Returns { materials: [...], total: number }
 */
export async function getMaterialsByInteriorType(interiorTypeId) {
  const id = String(interiorTypeId);
  // Avoid trailing slash before query to prevent 301 redirect (which breaks dev proxy/CORS)
  const path = `/api/v1/supplier-interiors/materials/by-interior-type?interior-type-id=${encodeURIComponent(id)}`;
  const res = await apiFetch(path, { method: 'GET' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Failed to load materials for interior type ${id} (${res.status})`);
  }
  const data = await res.json().catch(() => ({}));
  const list = Array.isArray(data.materials) ? data.materials : [];
  return {
    materials: list,
    total: typeof data.total === 'number' ? data.total : list.length,
  };
}

/**
 * Create a material via POST /supplier-interiors/materials.
 * Expects multipart/form-data with:
 * - material_type: string (interior type name)
 * - material_image: binary file (original upload)
 * @param {{ material_type: string, material_image: File | Blob }} payload
 * @returns {Promise<Response>}
 */
export async function createMaterial(payload) {
  const formData = new FormData();
  if (payload.material_type != null) {
    formData.append('material_type', payload.material_type);
  }
  if (payload.material_image != null) {
    formData.append('material_image', payload.material_image);
  }

  const res = await apiFetch(SUPPLIER_INTERIORS_ENDPOINTS.materials, {
    method: 'POST',
    body: formData,
  });
  return res;
}

/**
 * Create a product via POST /supplier-interiors/.
 * Multipart/form-data: name *, description (JSON string), interior_type_id * (one id or comma-separated list), material_ids (repeat field per id for List[int]), image (file), optional front/left/back/right.
 * @param {{
 *   name: string,
 *   description?: string,
 *   interior_type_id: string | number | (string|number)[],
 *   material_ids?: (string|number)[],
 *   image?: File | Blob | null,
 *   front?: File | Blob | null,
 *   left?: File | Blob | null,
 *   back?: File | Blob | null,
 *   right?: File | Blob | null,
 * }} payload
 * @returns {Promise<Response>}
 */
export async function createProduct(payload) {
  const interiorTypeIdsForLog = Array.isArray(payload.interior_type_id)
    ? payload.interior_type_id
    : payload.interior_type_id != null
      ? [payload.interior_type_id]
      : [];
  const logPayload = {
    name: payload.name,
    description: payload.description,
    interior_type_id: interiorTypeIdsForLog,
    material_ids: Array.isArray(payload.material_ids) ? payload.material_ids : payload.material_ids,
    image: payload.image != null ? { name: payload.image.name ?? '(blob)', size: payload.image.size } : null,
    front: payload.front != null ? { name: payload.front.name ?? '(blob)', size: payload.front.size } : null,
    left: payload.left != null ? { name: payload.left.name ?? '(blob)', size: payload.left.size } : null,
    back: payload.back != null ? { name: payload.back.name ?? '(blob)', size: payload.back.size } : null,
    right: payload.right != null ? { name: payload.right.name ?? '(blob)', size: payload.right.size } : null,
  };
  console.log('POST /supplier-interiors/ — sending:', logPayload);

  const formData = new FormData();
  if (payload.name != null) formData.append('name', String(payload.name));
  if (payload.description != null) formData.append('description', typeof payload.description === 'string' ? payload.description : JSON.stringify(payload.description));
  if (payload.interior_type_id != null) {
    const rawIds = Array.isArray(payload.interior_type_id)
      ? payload.interior_type_id
      : String(payload.interior_type_id)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
    if (rawIds.length > 0) {
      formData.append('interior_type_id', rawIds.map((id) => String(id)).join(','));
    }
  }
  const appendMaterialIdParts = (raw) => {
    const n = Number(raw);
    if (Number.isFinite(n)) formData.append('material_ids', String(Math.trunc(n)));
  };
  if (Array.isArray(payload.material_ids) && payload.material_ids.length > 0) {
    for (const id of payload.material_ids) appendMaterialIdParts(id);
  } else if (payload.material_ids != null && payload.material_ids !== '') {
    const str = typeof payload.material_ids === 'string' ? payload.material_ids : String(payload.material_ids);
    for (const part of str.split(',')) {
      const trimmed = part.trim();
      if (trimmed) appendMaterialIdParts(trimmed);
    }
  }
  if (payload.image != null) formData.append('image', payload.image);
  if (payload.front != null) formData.append('front', payload.front);
  if (payload.left != null) formData.append('left', payload.left);
  if (payload.back != null) formData.append('back', payload.back);
  if (payload.right != null) formData.append('right', payload.right);

  const res = await apiFetch(SUPPLIER_INTERIORS_ENDPOINTS.products, {
    method: 'POST',
    body: formData,
  });
  return res;
}

/**
 * Delete a supplier interior / product.
 * DELETE /api/v1/supplier-interiors/{interior_id}
 * @param {number|string} interiorId
 */
export async function deleteSupplierInterior(interiorId) {
  const path = SUPPLIER_INTERIORS_ENDPOINTS.interiorById(interiorId);
  const res = await apiFetch(path, { method: 'DELETE' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let message = text || `Failed to delete product (${res.status})`;
    try {
      const parsed = JSON.parse(text);
      message = parsed.message || parsed.error || message;
    } catch {
      /* plain text body */
    }
    throw new Error(message);
  }
  if (res.status === 204) return null;
  return res.json().catch(() => ({}));
}
